/* eslint-disable complexity */
import React, { useContext, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { isNull } from 'lodash';

import { DataSource, ProductSetup } from '@futrli-sdk/organisations';
import {
  getConnectedDatasource,
  getForecastingByIdRouteUrl,
  getProductUrl,
  PRODUCT_ROUTES,
  ROUTES
} from '@futrli/shared-services';
import { UILoader } from '@futrli/ui-components';

import { checkForData } from '../../core/api/forecast';
import { DetailContainer } from '../../forecast/detail/containers/detail-container';
import { ExportRedirect } from '../../forecast/detail/reports/external-sheets/export/export-redirect';
import { ImportRedirect } from '../../forecast/detail/reports/external-sheets/import/containers/import-redirect/import-redirect';
import { InitialOnboardingLoader } from '../../../../skeleton/src/onboarding/components/initial-onboarding-nav';
import { NonFinancialDrivers } from '../../non-financial-drivers/non-financial-drivers';
import { NoDataError } from '../../ui/no-data-error/no-data-error';
import { NoDatasourceConnected } from '../../ui/datasource-status-message/no-datasource-connected';
import { OrgAccessForbiddenMessage } from '../../ui/org-access-forbidden-message/org-access-forbidden-message';
import { PredictManagementContext } from '../context/predict-management-context';
import { PredictSyncingProgress, shouldShowSyncingProgressPage } from './predict-syncing-progress';
import { PredictTrialExpired } from './predict-trial-expired';
import { useOnboardingUrlInfo } from '../../forecast/onboarding/utils/hooks';
import { useProductStatus } from '../hooks/useProductStatus/useProductStatus';
import { WorkspaceStatusPending } from '../../ui/workspace-status-message/workspace-status-pending';

export interface Props {
  readonly productSetup: ProductSetup;
}

export const PredictManagement: React.FC<Props> = ({ productSetup }): JSX.Element => {
  const { isOnboardingRoute, onboardingForecastId } = useOnboardingUrlInfo();
  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisationId } = useParams();
  const workspaceId = tempWorkspaceId || '';
  const organisationId = tempOrganisationId || '';
  const { isPro, isSubscriptionStatePending } = useProductStatus(productSetup);
  const [hasData, setHasData] = useState<boolean | null>(null);
  const { organisation } = useContext(PredictManagementContext);
  const connectedDatasource = organisation ? getConnectedDatasource(organisation) : undefined;
  const hasDSConnected = hasProductAnyDSConnected(connectedDatasource);
  const [isCheckingDataExists, setIsCheckingDataExists] = useState(!isOnboardingRoute);
  const baseUrl = getProductUrl(workspaceId, organisationId, PRODUCT_ROUTES.PREDICT);

  useEffect(() => {
    const checkDataExists = async () => {
      if (organisation) {
        try {
          setIsCheckingDataExists(true);
          const report = await checkForData(workspaceId, organisationId);
          setHasData(Boolean(report));
        } catch (e) {
          setHasData(false);
        } finally {
          setIsCheckingDataExists(false);
        }
      }
    };
    if (!isOnboardingRoute) {
      checkDataExists();
    }
  }, [organisation, isOnboardingRoute]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isNull(isPro) || isCheckingDataExists) {
    if (isOnboardingRoute || onboardingForecastId) {
      return <InitialOnboardingLoader />;
    } else {
      return <UILoader isFull={true} />;
    }
  }

  if (isSubscriptionStatePending) {
    return <WorkspaceStatusPending />;
  }

  if (!hasDSConnected) {
    return <NoDatasourceConnected />;
  }

  if (!organisation?.can_be_accessed) {
    return <OrgAccessForbiddenMessage />;
  }

  if (!isPro) {
    return (
      <Routes>
        <Route path={ROUTES.TRIAL_EXPIRED} element={<PredictTrialExpired />} />
        <Route path="*" element={<Navigate to={`${baseUrl}${ROUTES.TRIAL_EXPIRED}`} />} />
      </Routes>
    );
  }

  if (hasData === false && shouldShowSyncingProgressPage(connectedDatasource)) {
    return (
      <Routes>
        <Route path={ROUTES.SYNCING_PROGRESS} element={<PredictSyncingProgress />} />
        <Route path="*" element={<Navigate to={`${baseUrl}${ROUTES.SYNCING_PROGRESS}`} />} />
      </Routes>
    );
  }

  if (hasData === false) {
    return <NoDataError />;
  }

  return (
    <React.Fragment>
      <Routes>
        <Route path={ROUTES.NON_FINANCIAL_DRIVERS} element={<NonFinancialDrivers />} />
        <Route path={`${ROUTES.FORECASTS}${ROUTES.FORECAST_BY_ID}${ROUTES.EXPORT}`} element={<ExportRedirect />} />
        <Route
          path={`${ROUTES.FORECASTS}${ROUTES.FORECAST_BY_ID}${ROUTES.IMPORT_REDIRECT}`}
          element={<ImportRedirect />}
        />
        <Route path="/*" element={<DetailContainer isOnboardingPage={isOnboardingRoute} workspaceId={workspaceId} />} />

        {onboardingForecastId && (
          <Route
            path="*"
            element={<Navigate to={getForecastingByIdRouteUrl(workspaceId, organisationId, onboardingForecastId)} />}
          />
        )}
      </Routes>
    </React.Fragment>
  );
};

export const hasProductAnyDSConnected = (connectedDS?: DataSource) => Boolean(connectedDS);
